import { Activity, AlertTriangle, HeartPulse, ShieldAlert, Sparkles } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import type { SessionContextSnapshot, ContextRiskLevel } from "@/domain/sessionContextEngine";

const riskLabel: Record<ContextRiskLevel, string> = {
  monitorar: "Monitorar",
  cautela: "Cautela",
  relevante: "Risco relevante",
  alto: "Alto risco",
  emergencia: "Emergência",
};

const riskClass: Record<ContextRiskLevel, string> = {
  monitorar: "border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-300",
  cautela: "border-yellow-500/30 bg-yellow-500/10 text-yellow-700 dark:text-yellow-300",
  relevante: "border-orange-500/30 bg-orange-500/10 text-orange-700 dark:text-orange-300",
  alto: "border-red-500/35 bg-red-500/10 text-red-700 dark:text-red-300",
  emergencia: "border-red-700/50 bg-red-700/15 text-red-800 dark:text-red-200",
};

function amount(value: number, unit: string) {
  const digits = value >= 100 ? 0 : value >= 10 ? 1 : 2;
  return `${value.toFixed(digits)} ${unit}`;
}

export function SessionContextPanels({ snapshot }: { snapshot: SessionContextSnapshot }) {
  return (
    <div className="space-y-5">
      <section className="space-y-2">
        <div className="px-1">
          <h2 className="text-lg font-semibold">Total no corpo · estimativa</h2>
          <p className="text-xs text-muted-foreground">Quantidade parent-equivalente estimada. Não é concentração sérica nem intensidade subjetiva.</p>
        </div>
        <Card className="overflow-hidden rounded-3xl">
          {snapshot.states.length ? (
            <div className="divide-y">
              {snapshot.states.map((state) => (
                <div key={state.key} className="space-y-3 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="h-2.5 w-2.5 rounded-full bg-primary" />
                        <div className="font-semibold">{state.name}</div>
                        {state.carryOver ? <Badge variant="outline" className="text-[9px]">carry-over</Badge> : null}
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground">Fase: {state.phase} · efeito relativo {state.effect0to100.toFixed(0)}/100</div>
                    </div>
                    {state.body ? (
                      <div className="shrink-0 text-right">
                        <div className="font-semibold tabular-nums">≈ {amount(state.body.remainingMid, state.body.unit)}</div>
                        <div className="text-[10px] text-muted-foreground">faixa {amount(state.body.remainingLow, state.body.unit)}–{amount(state.body.remainingHigh, state.body.unit)}</div>
                      </div>
                    ) : <Badge variant="outline">PK insuficiente</Badge>}
                  </div>
                  {state.body ? (
                    <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                      <div className="h-full rounded-full bg-primary" style={{ width: `${Math.max(2, Math.min(100, state.body.remainingFraction * 100))}%` }} />
                    </div>
                  ) : null}
                  <div className="rounded-2xl border bg-muted/20 p-3">
                    <div className="text-xs font-medium">Tolerância · {state.tolerance.label}</div>
                    <div className="mt-1 text-[11px] leading-relaxed text-muted-foreground">{state.tolerance.reason}</div>
                    <div className="mt-1 text-[10px] text-muted-foreground">Confiança {state.tolerance.confidence}. Não representa percentual fisiológico.</div>
                  </div>
                </div>
              ))}
            </div>
          ) : <div className="p-5 text-sm text-muted-foreground">Nenhuma substância com atividade/carga estimável nesta referência temporal.</div>}
        </Card>
      </section>

      <section className="space-y-2">
        <div className="flex items-center gap-2 px-1"><ShieldAlert className="h-5 w-5" /><h2 className="text-lg font-semibold">Interações neste momento</h2></div>
        {!snapshot.interactions.length ? (
          <Card className="rounded-3xl p-4 text-sm text-muted-foreground">Nenhuma regra contextual relevante foi acionada. Isso não equivale a “seguro” nem exclui interação desconhecida.</Card>
        ) : (
          <div className="space-y-3">
            {snapshot.interactions.map((interaction) => (
              <Card key={interaction.id} className="rounded-3xl p-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold leading-snug">{interaction.title}</div>
                    <div className="mt-1 text-xs text-muted-foreground">{interaction.involved.join(" + ")}</div>
                  </div>
                  <Badge variant="outline" className={riskClass[interaction.level]}><AlertTriangle className="mr-1 h-3 w-3" />{riskLabel[interaction.level]}</Badge>
                </div>
                <p className="mt-3 text-sm leading-relaxed">{interaction.rationale}</p>
                <div className="mt-3 rounded-2xl border bg-muted/20 p-3 text-xs">
                  <div><b>Por quê:</b> {interaction.mechanism}</div>
                  <div className="mt-2 text-muted-foreground"><b>Contexto usado:</b> {interaction.context}</div>
                </div>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <div><div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Monitorar</div><div className="mt-1 text-xs">{interaction.monitor.join(" · ")}</div></div>
                  <div><div className="text-[10px] font-semibold uppercase tracking-wide text-red-600 dark:text-red-300">Sinais de alarme</div><div className="mt-1 text-xs">{interaction.redFlags.join(" · ")}</div></div>
                </div>
                <div className="mt-2 text-[10px] text-muted-foreground">Confiança {interaction.confidence}; classificação contextual, não diagnóstico.</div>
              </Card>
            ))}
          </div>
        )}
      </section>

      <section className="space-y-2">
        <div className="flex items-center gap-2 px-1"><Activity className="h-5 w-5" /><h2 className="text-lg font-semibold">Efeitos e sinais a observar</h2></div>
        <div className="grid gap-2 sm:grid-cols-2">
          {snapshot.effects.length ? snapshot.effects.map((effect) => (
            <Card key={effect.id} className="rounded-3xl p-4">
              <div className="flex items-start justify-between gap-2"><div className="font-medium">{effect.label}</div><Badge variant="outline">{effect.status}</Badge></div>
              <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{effect.rationale}</p>
            </Card>
          )) : <Card className="rounded-3xl p-4 text-sm text-muted-foreground">Sem previsão qualitativa suficientemente sustentada para esta referência.</Card>}
        </div>
      </section>

      <section className="space-y-2">
        <div className="flex items-center gap-2 px-1"><HeartPulse className="h-5 w-5" /><h2 className="text-lg font-semibold">Redução de danos / recuperação</h2></div>
        <Card className="overflow-hidden rounded-3xl">
          {snapshot.recovery.length ? <div className="divide-y">{snapshot.recovery.map((tip) => (
            <div key={tip.id} className="flex gap-3 p-4">
              <div className={`mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-full ${tip.urgent ? "bg-red-500/10 text-red-600" : "bg-primary/10 text-primary"}`}>{tip.urgent ? <ShieldAlert className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}</div>
              <div><div className="font-medium">{tip.title}</div><p className="mt-1 text-xs leading-relaxed text-muted-foreground">{tip.text}</p></div>
            </div>
          ))}</div> : <div className="p-4 text-sm text-muted-foreground">Nenhuma orientação contextual adicional foi acionada.</div>}
        </Card>
      </section>

      <div className="rounded-2xl border border-dashed p-3 text-[10px] leading-relaxed text-muted-foreground">
        {snapshot.notes.join(" ")}
      </div>
    </div>
  );
}
